import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Radio, Search, CheckCircle, Truck, MapPin, Clock, Heart, ArrowDown } from 'lucide-react';
import { Building2 } from 'lucide-react';

const EcosystemMindMap = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const nodes = [
    { icon: Building2, text: "Hospital/Blood Bank Request" },
    { icon: Radio, text: "AI Geo-Ping Engine" },
    { icon: Search, text: "Nearest Verified Match" },
    { icon: Truck, text: "Multimodal Dispatch" },
    { icon: MapPin, text: "Live Tracking + Compliance" },
    { icon: Clock, text: "Faster Treatment Window" },
    { icon: Heart, text: "Lives Saved" }
  ];

  return (
    <section ref={ref} className="py-32 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-7xl font-bold mb-6 text-black leading-tight">
            The SupaAid
            <br />
            <span className="text-blue-600">Ecosystem</span>
          </h2>
          <p className="text-xl text-gray-600">End-to-end emergency medical coordination</p>
        </motion.div>

        {/* 3D Mind Map */}
        <div className="relative">
          {nodes.map((node, index) => {
            const Icon = node.icon;
            const isLast = index === nodes.length - 1;
            
            return (
              <div key={index} className="mb-8 last:mb-0">
                {/* Node Card */}
                <motion.div
                  initial={{ opacity: 0, x: -50, rotateY: -20 }}
                  animate={isInView ? { opacity: 1, x: 0, rotateY: 0 } : {}}
                  transition={{ delay: 0.2 + index * 0.15, duration: 0.6 }}
                  whileHover={{ 
                    scale: 1.03, 
                    rotateY: 5,
                    z: 50
                  }}
                  className="relative mx-auto max-w-xl"
                  style={{ transformStyle: "preserve-3d" }}
                >
                  <div className="bg-white backdrop-blur-xl border-2 border-blue-200 p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all relative overflow-hidden">
                    {/* Glass Effect */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-transparent opacity-60" />
                    
                    {/* Shine on Hover */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: "100%" }}
                      transition={{ duration: 0.6 }}
                    />
                    
                    <div className="flex items-center justify-center gap-6 relative z-10">
                      <div className="bg-blue-100 rounded-2xl p-4">
                        <Icon className="w-10 h-10 text-blue-600" />
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900">{node.text}</h3>
                    </div>
                  </div>
                </motion.div>

                {/* Connector Arrow */}
                {!isLast && (
                  <motion.div
                    className="relative h-16 flex justify-center"
                    initial={{ scaleY: 0, opacity: 0 }}
                    animate={isInView ? { scaleY: 1, opacity: 1 } : {}}
                    transition={{ delay: 0.3 + index * 0.15, duration: 0.5 }}
                  >
                    <div className="relative">
                      <ArrowDown className="w-8 h-8 text-blue-600" />
                      
                      {/* Flowing dots */}
                      <motion.div
                        className="absolute left-1/2 transform -translate-x-1/2 w-3 h-3 bg-blue-500 rounded-full"
                        animate={{ y: [0, 64, 0] }}
                        transition={{ 
                          duration: 2, 
                          repeat: Infinity,
                          delay: index * 0.3
                        }}
                      />
                    </div>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.5 }}
          className="mt-16 text-center"
        >
          <div className="inline-block bg-blue-50 backdrop-blur-xl border border-blue-200 rounded-2xl px-8 py-4">
            <p className="text-gray-700 font-medium italic">
              Citizen AI triage strengthens demand intelligence
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EcosystemMindMap;
